import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { EventAvailable, Visibility, Edit } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const AMCSchedule = () => {
  const history = useHistory();
  const [filters, setFilters] = useState({ search: '', frequency: '', month: '' });
  const frequencies = ['Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const initialRows = useMemo(() => ([
    { amcId: 'AMC-1205', customerId: 'C-102', customerName: 'Ramesh Kumar', product: 'Solar Panel 5kW', installationDate: '2024-10-20', amcStart: '2024-10-20', amcEnd: '2025-10-20', frequency: 'Quarterly', nextServiceDate: '2025-11-30', technicianId: 'T-004', technicianName: 'Suresh Yadav', technicianPhone: '+91 98765 00004', status: 'Upcoming', lastServiceDate: '2025-08-20', remarks: '' },
    { amcId: 'AMC-1210', customerId: 'C-108', customerName: 'Priya Sharma', product: 'Inverter 3kW', installationDate: '2024-09-12', amcStart: '2024-09-12', amcEnd: '2025-09-12', frequency: 'Yearly', nextServiceDate: '2025-09-10', technicianId: 'T-002', technicianName: 'Riya Sharma', technicianPhone: '+91 90000 00002', status: 'Scheduled', lastServiceDate: '2024-09-12', remarks: '' },
  ]), []);
  const [rows, setRows] = useState(initialRows);
  const [add, setAdd] = useState({ open: false, ref: '', project: '', due: '', frequency: '', last: '' });

  const technicians = useMemo(() => ([
    { id: 'T-001', name: 'Amit Kumar', phone: '+91 90000 00001' },
    { id: 'T-002', name: 'Riya Sharma', phone: '+91 90000 00002' },
    { id: 'T-004', name: 'Suresh Yadav', phone: '+91 98765 00004' },
  ]), []);

  const [open, setOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [form, setForm] = useState({
    amcId: '',
    customerId: '',
    customerName: '',
    product: '',
    installationDate: '',
    amcStart: '',
    amcEnd: '',
    frequency: '',
    nextServiceDate: '',
    technicianId: '',
    technicianName: '',
    technicianPhone: '',
    maintenanceType: 'Preventive',
    status: 'Upcoming',
    lastServiceDate: '',
    remarks: '',
    reportUrl: '',
  });

  const openDialog = (row, isView = false) => {
    setForm({
      amcId: row.amcId,
      customerId: row.customerId,
      customerName: row.customerName,
      product: row.product,
      installationDate: row.installationDate,
      amcStart: row.amcStart,
      amcEnd: row.amcEnd,
      frequency: row.frequency,
      nextServiceDate: row.nextServiceDate,
      technicianId: row.technicianId,
      technicianName: row.technicianName,
      technicianPhone: row.technicianPhone,
      maintenanceType: row.maintenanceType || 'Preventive',
      status: row.status,
      lastServiceDate: row.lastServiceDate,
      remarks: row.remarks || '',
      reportUrl: row.reportUrl || '',
    });
    setViewOnly(isView);
    setOpen(true);
  };

  const computeAmcEnd = (start, freq) => {
    if (!start) return '';
    const d = new Date(start);
    if (freq === 'Yearly') d.setFullYear(d.getFullYear() + 1);
    if (freq === 'Half-Yearly') d.setMonth(d.getMonth() + 6);
    if (freq === 'Quarterly') d.setMonth(d.getMonth() + 3);
    if (freq === 'Monthly') d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  };

  const saveForm = () => {
    setRows(prev => prev.map(r => r.amcId === form.amcId ? {
      ...r,
      customerId: form.customerId,
      customerName: form.customerName,
      product: form.product,
      installationDate: form.installationDate,
      amcStart: form.amcStart,
      amcEnd: form.amcEnd,
      frequency: form.frequency,
      nextServiceDate: form.nextServiceDate,
      technicianId: form.technicianId,
      technicianName: form.technicianName,
      technicianPhone: form.technicianPhone,
      maintenanceType: form.maintenanceType,
      status: form.status,
      lastServiceDate: form.lastServiceDate,
      remarks: form.remarks,
      reportUrl: form.reportUrl,
    } : r));
    setOpen(false);
  };

  const filtered = rows.filter(r =>
    (!filters.search || `${r.ref} ${r.project}`.toLowerCase().includes(filters.search.toLowerCase())) &&
    (!filters.frequency || r.frequency === filters.frequency)
  );

  const colorFor = (s) => (s === 'Scheduled' ? 'success' : 'default');

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>AMC / Maintenance Schedule</Typography>
            <Typography variant="body2" color="text.secondary">Plan and view maintenance schedules</Typography>
          </Box>
          <Button startIcon={<EventAvailable />} variant="outlined" onClick={() => history.push('/app/green-energy/service/amc-schedule/create')}>Add Schedule</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Search (Ref / Project)" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequency</InputLabel>
                <Select value={filters.frequency} label="Frequency" onChange={(e) => setFilters(prev => ({ ...prev, frequency: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  {frequencies.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select value={filters.month} label="Month" onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  {months.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          {add.open && (
            <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, backgroundColor: '#fafafa' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <TextField size="small" fullWidth label="Ref" value={add.ref} onChange={(e) => setAdd(prev => ({ ...prev, ref: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField size="small" fullWidth label="Project" value={add.project} onChange={(e) => setAdd(prev => ({ ...prev, project: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField size="small" fullWidth type="date" label="Due" InputLabelProps={{ shrink: true }} value={add.due} onChange={(e) => setAdd(prev => ({ ...prev, due: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Frequency</InputLabel>
                    <Select value={add.frequency} label="Frequency" onChange={(e) => setAdd(prev => ({ ...prev, frequency: e.target.value }))}>
                      {frequencies.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField size="small" fullWidth type="date" label="Last" InputLabelProps={{ shrink: true }} value={add.last} onChange={(e) => setAdd(prev => ({ ...prev, last: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Box display="flex" gap={1}>
                    <Button variant="contained" size="small" onClick={() => {
                      if (!add.ref || !add.project || !add.due || !add.frequency) { alert('Fill required fields'); return; }
                      setRows(prev => ([...prev, { ref: add.ref, project: add.project, due: add.due, frequency: add.frequency, last: add.last, status: 'Scheduled' }]));
                      setAdd({ open: false, ref: '', project: '', due: '', frequency: '', last: '' });
                    }}>Save</Button>
                    <Button variant="text" size="small" onClick={() => setAdd({ open: false, ref: '', project: '', due: '', frequency: '', last: '' })}>Cancel</Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Product</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Technician</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Next Service</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Frequency</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.ref} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{r.customerName}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.product}</TableCell>
                    <TableCell>{r.technicianName}</TableCell>
                    <TableCell>{r.nextServiceDate}</TableCell>
                    <TableCell><Chip label={r.frequency} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={r.status} size="small" color={colorFor(r.status)} /></TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<Visibility />} onClick={() => openDialog(r, true)}>View</Button>
                      <Button size="small" startIcon={<Edit />} onClick={() => openDialog(r, false)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* View / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>AMC / Maintenance Schedule • {form.amcId}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Customer Name" value={form.customerName} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Customer ID" value={form.customerId} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Product / System" value={form.product} onChange={(e) => setForm(prev => ({ ...prev, product: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Installation Date" value={form.installationDate} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="AMC Start Date" InputLabelProps={{ shrink: true }} value={form.amcStart} onChange={(e) => {
                const amcStart = e.target.value;
                const amcEnd = computeAmcEnd(amcStart, form.frequency);
                setForm(prev => ({ ...prev, amcStart, amcEnd }));
              }} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="AMC End Date" InputLabelProps={{ shrink: true }} value={form.amcEnd} onChange={(e) => setForm(prev => ({ ...prev, amcEnd: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Service Frequency</InputLabel>
                <Select value={form.frequency} label="Service Frequency" onChange={(e) => {
                  const frequency = e.target.value;
                  const amcEnd = computeAmcEnd(form.amcStart, frequency);
                  setForm(prev => ({ ...prev, frequency, amcEnd }));
                }} disabled={viewOnly}>
                  {frequencies.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="Next Service Date" InputLabelProps={{ shrink: true }} value={form.nextServiceDate} onChange={(e) => setForm(prev => ({ ...prev, nextServiceDate: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={technicians}
                getOptionLabel={(o) => o ? `${o.name} • ${o.phone}` : ''}
                value={technicians.find(t => t.id === form.technicianId) || null}
                onChange={(_, value) => setForm(prev => ({ ...prev, technicianId: value?.id || '', technicianName: value?.name || '', technicianPhone: value?.phone || '' }))}
                renderInput={(params) => <TextField {...params} label="Technician Assigned" />}
                disabled={viewOnly}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Technician Contact" value={form.technicianPhone} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Maintenance Type</InputLabel>
                <Select value={form.maintenanceType} label="Maintenance Type" onChange={(e) => setForm(prev => ({ ...prev, maintenanceType: e.target.value }))} disabled={viewOnly}>
                  <MenuItem value="Preventive">Preventive</MenuItem>
                  <MenuItem value="Corrective">Corrective</MenuItem>
                  <MenuItem value="Emergency">Emergency</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))} disabled={viewOnly}>
                  <MenuItem value="Upcoming">Upcoming</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="Last Service Date" InputLabelProps={{ shrink: true }} value={form.lastServiceDate} onChange={(e) => setForm(prev => ({ ...prev, lastServiceDate: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Remarks / Notes" value={form.remarks} onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" disabled={viewOnly}>Upload Report / Invoice</Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {!viewOnly && (
            <Button variant="contained" onClick={saveForm} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Save Schedule</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AMCSchedule;



